import type { ReactElement } from "react"
import styled from "styled-components"
import { oppositeOf } from "~/shared/theme/mode"
import { useThemeMode } from "~/shared/theme/useThemeMode"
import { Moon, Sun } from "./icons/ThemeMode"

const Control = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  background-color: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textSecondary};
  cursor: pointer;
  transition:
    background-color 120ms ease-in-out,
    color 120ms ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.color.surfaceSubtle};
    color: ${({ theme }) => theme.color.textPrimary};
  }
`

interface ThemeToggleProps {
  /** Defaults so the control is drop-in wherever a header needs it. */
  readonly qaHook?: string
}

/**
 * Switches the app between the light and dark palettes.
 *
 * Shows the mode it would switch *to*, not the one in force — the sun means
 * "make it light", which is the question a toggle is actually answering. The
 * glyph alone would be ambiguous either way, so the accessible name states the
 * action outright rather than describing the picture.
 */
export const ThemeToggle = ({ qaHook = "theme-toggle" }: ThemeToggleProps): ReactElement => {
  const { mode, toggle } = useThemeMode()
  const next = oppositeOf(mode)

  return (
    <Control
      type="button"
      onClick={toggle}
      aria-label={`Switch to ${next} theme`}
      title={`Switch to ${next} theme`}
      data-qa-hook={qaHook}
      data-theme-mode={mode}
    >
      {mode === "dark" ? <Sun /> : <Moon />}
    </Control>
  )
}
